import React, { useEffect, useState } from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { useSelector } from "react-redux"
import _ from "lodash"

import { useModal } from "@/helpers/useModal"
import {
  canEdit,
  canEditActiveBid,
  getFeatures,
  getFormsEditable,
  getFormsExpanded,
  getSectionSettings,
  isBidUnderReview,
} from "@/onboarding/features/product/selectors"
import { SubmittingContext, useProductSubmitting } from "@/onboarding/features/product/helpers"
import { BlueLock, GreenCheckMark, OrangeExclamation, YellowExchange } from "@/components/Icons"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import { ChatButton } from "@/components/chat"

import EditIcon from "./EditIcon"
import EditModal from "./EditModal"
import Importer from "./Importer"
import ApplyToToursButton from "./apply_to_tours/ApplyToToursButton"
import iconA11y from "@/components/iconA11y"

const NullEditComponent = ({ setEditing }): JSX.Element => null

const SectionHolder = ({
  title,
  name,
  editComponent = null,
  editMethod = "modal",
  showLockedIcon = false,
  importableFields = [],
  incompleteRequiredFieldCount = 0,
  proposedChangesCount = 0,
  missingRequiredComponent = false,
  changedComponent = false,
  chat = null,
  children,
}): JSX.Element => {
  const section = useSelector(getSectionSettings(name))
  const canEditView = useSelector(canEdit)
  const editableBid = useSelector(canEditActiveBid)
  const underReview = useSelector(isBidUnderReview)
  // The owning operator can edit their REQUESTED/DRAFT bid (editableBid). Otherwise
  // defer to section.writeable, EXCEPT while a submitted bid is under review:
  // section.writeable is true for a CL owner on always-editable sections
  // (description, settlement, tour_stops, tour_images) regardless of bid state and
  // must not expose edit controls on the submission. Contracting/contracted tours
  // have no submitted bid in context, so the CL retains editing there.
  const editable = editableBid || (!underReview && section?.writeable === true)

  const {
    general: {
      product: { comment: showComment },
    },
  } = useSelector(getFeatures)
  const formsEditable = useSelector(getFormsEditable)
  const formsExpanded = useSelector(getFormsExpanded)

  const editingInitialState = editMethod === "modal" ? false : formsEditable
  const showEditIcon = !_.isNull(editComponent) && editable
  const showImportIcon = !_.isEmpty(importableFields) && editable
  const showSectionLockIcon = showLockedIcon && canEditView
  const includeRequiredWarning = incompleteRequiredFieldCount > 0 || missingRequiredComponent

  const [editing, setEditing] = useState(editingInitialState)
  const [collapsed, setCollapsed] = useState(!formsExpanded)
  const [importing, setImporting] = useState(false)
  const [submitting, setSubmitting] = useProductSubmitting()

  useEffect(() => {
    if (editMethod !== "modal" && editing !== formsEditable) {
      setEditing(formsEditable)
    }
    setCollapsed(!formsExpanded)
  }, [formsEditable, formsExpanded])

  const toggleCollapsed = () => {
    setCollapsed(!collapsed)
  }

  const onEdit =
    editMethod === "modal"
      ? useModal((props) => <EditModal {...props} title={title} Body={editComponent} />)
      : () => setEditing(true)

  const EditComponent = editComponent || NullEditComponent

  const requiredWarningText = missingRequiredComponent
    ? "At least one component is required."
    : `The number of incomplete required fields: ${incompleteRequiredFieldCount}`

  const proposedChangesText = changedComponent
    ? "Components have been updated. You can toggle between the original and updated values."
    : `${proposedChangesCount} changed field(s). Click a field name to toggle between the original and updated values.`

  const ProposedChangesTooltip = (
    <Tooltip id="proposed-changes-tooltip" className="ms-5 mt-n1">
      {proposedChangesText}
    </Tooltip>
  )

  let body

  if (importing) {
    body = <Importer setImporting={setImporting} fieldNames={importableFields} />
  } else if (editing) {
    body = <EditComponent setEditing={setEditing} />
  } else if (!collapsed) {
    body = children
  } else {
    body = null
  }

  return (
    <SubmittingContext.Provider value={[submitting, setSubmitting]}>
      <div className="d-flex flex-column gap-2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {showSectionLockIcon && (
              <BlueLock
                className="me-2 cursor-pointer"
                data-toggle="tooltip"
                data-placement="top"
                title="This component is fixed"
              />
            )}
            <h5 className="mb-0 cursor-pointer d-flex" onClick={toggleCollapsed}>
              <OperatorOnly>
                <div key={`${title}-${incompleteRequiredFieldCount}`}>
                  {includeRequiredWarning && (
                    <OrangeExclamation
                      data-toggle="tooltip"
                      data-placement="top"
                      title={requiredWarningText}
                      className="me-2"
                    />
                  )}
                  {!includeRequiredWarning && (
                    <GreenCheckMark
                      data-toggle="tooltip"
                      data-placement="top"
                      title="All required fields have been completed"
                      className="me-2"
                    />
                  )}
                </div>
              </OperatorOnly>
              {(proposedChangesCount > 0 || changedComponent) && (
                <OverlayTrigger placement="bottom" overlay={ProposedChangesTooltip}>
                  <span>
                    <YellowExchange className="me-2 cursor-pointer" />
                  </span>
                </OverlayTrigger>
              )}
              <span>{title}</span>
              <span className={collapsed ? "" : "d-none"}>
                <i key="down-icon" className="fas fa-chevron-down ms-2" />
              </span>
              <span className={collapsed ? "d-none" : ""}>
                <i key="up-icon" className="fas fa-chevron-up ms-2" />
              </span>
            </h5>
          </div>
          <div className="d-flex gap-2">
            {showEditIcon && !editing && <EditIcon onEdit={onEdit} />}
            {editing && (
              <span className="cursor-pointer" onClick={() => setEditing(false)}>
                <i className="fas fa-times text-danger" />
              </span>
            )}
            {showImportIcon && !importing && (
              <span className="cursor-pointer" onClick={() => setImporting(true)}>
                <i className="fas fa-file-import" {...iconA11y("Import")} />
              </span>
            )}
            {importing && (
              <span className="cursor-pointer" onClick={() => setImporting(false)}>
                <i className="fas fa-times text-danger" />
              </span>
            )}
            <ApplyToToursButton
              fieldNames={importableFields}
              sectionName={name}
              sectionTitle={title}
            />
            {chat && (
              <div
                style={{
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                  wordBreak: "break-word",
                }}
                key={chat.id}
                className="mt-n1 ms-2"
              >
                <ChatButton chat={chat} key={chat.id} />
              </div>
            )}
          </div>
        </div>
        {!collapsed && <div>{body}</div>}
      </div>
    </SubmittingContext.Provider>
  )
}

export default SectionHolder
